import React from 'react';
import type { GoalMetric, GoalResponse } from '@troia-v3/types';
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';
import {
  ASSIGNEE_SUBLABEL,
  METRIC_ICON,
  METRIC_LABEL,
  PERIOD_LABEL,
  formatPeriodRange,
  formatTarget,
  getAssigneeInitials,
  isCompanyGoal,
} from '../utils/goalDisplay';

/**
 * Linha de tabela de meta (view "Lista"). Padrão-lista travado:
 * checkbox de seleção (1ª col) + célula multi-linha (responsável + tipo) + menu ••• (última col).
 * Avatar sem foto = slate (#64748b) via AvatarFallback.
 */
export interface GoalListItemProps {
  goal: GoalResponse;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
  onEdit?: (goal: GoalResponse) => void;
  onDelete?: (goal: GoalResponse) => void;
  /** "Ver progresso" — navega pro dashboard comercial */
  onViewProgress?: (goal: GoalResponse) => void;
  /** "Duplicar" — abre o drawer pré-preenchido pro próximo período */
  onDuplicate?: (goal: GoalResponse) => void;
}

export const GoalListItem: React.FC<GoalListItemProps> = ({
  goal,
  selected = false,
  onToggleSelect,
  onEdit,
  onDelete,
  onViewProgress,
  onDuplicate,
}) => {
  const metric: GoalMetric = goal.metric;
  const assigneePrimary = goal.assigneeName ?? (isCompanyGoal(goal) ? 'Empresa' : '—');
  const assigneeIcon: IconName | null = isCompanyGoal(goal)
    ? 'globe'
    : goal.assigneeType === 'team'
      ? 'users'
      : null;

  const actions: CardAction[] = [];
  if (onEdit) actions.push({ label: 'Editar', icon: 'edit', onClick: () => onEdit(goal) });
  if (onViewProgress) actions.push({ label: 'Ver progresso', icon: 'trending-up', onClick: () => onViewProgress(goal) });
  if (onDuplicate) actions.push({ label: 'Duplicar', icon: 'copy', onClick: () => onDuplicate(goal) });
  if (onDelete) actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onDelete(goal), variant: 'danger', dividerBefore: true });

  return (
    <tr
      className="border-b transition-colors"
      style={{
        borderColor: 'var(--theme-card-border)',
        backgroundColor: selected ? 'var(--theme-primary-background)' : undefined,
      }}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'transparent'; }}
      data-testid={`goals-row-${goal.id}`}
    >
      {/* Checkbox */}
      <td className="pl-4 pr-1 py-3 w-11">
        <button
          type="button"
          onClick={() => onToggleSelect?.(goal.id)}
          className="w-[18px] h-[18px] rounded-[5px] border-[1.6px] flex items-center justify-center transition-colors"
          style={{
            backgroundColor: selected ? 'var(--theme-primary)' : 'transparent',
            borderColor: selected ? 'var(--theme-primary)' : 'var(--theme-input-border)',
            color: '#ffffff',
          }}
          aria-label={selected ? 'Desmarcar' : 'Selecionar'}
          data-testid={`goals-check-${goal.id}`}
        >
          {selected && <Icon name="check" className="w-3 h-3" />}
        </button>
      </td>

      {/* Responsável: avatar (slate) + nome + tipo */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <Avatar className="w-9 h-9" isSquircle>
            <AvatarFallback isSquircle>
              {assigneeIcon ? (
                <Icon name={assigneeIcon} className="w-4 h-4 text-white" />
              ) : (
                <span className="text-white font-semibold text-xs">{getAssigneeInitials(goal)}</span>
              )}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="font-semibold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
              {assigneePrimary}
            </div>
            <div className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
              {ASSIGNEE_SUBLABEL[goal.assigneeType]}
            </div>
          </div>
        </div>
      </td>

      {/* Métrica */}
      <td className="px-4 py-3">
        <span
          className="inline-flex items-center gap-1.5 h-[24px] px-2.5 rounded-lg text-xs font-semibold whitespace-nowrap"
          style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-card-text)' }}
        >
          <Icon name={METRIC_ICON[metric]} className="w-3.5 h-3.5" style={{ color: 'var(--theme-muted-text)' }} />
          {METRIC_LABEL[metric]}
        </span>
      </td>

      {/* Período */}
      <td className="px-4 py-3 text-sm" style={{ color: 'var(--theme-card-text)' }}>
        {PERIOD_LABEL[goal.period] ?? goal.period}
      </td>

      {/* Janela (range de datas) */}
      <td className="px-4 py-3 text-sm whitespace-nowrap" style={{ color: 'var(--theme-muted-text)' }}>
        {formatPeriodRange(goal.periodStart, goal.periodEnd)}
      </td>

      {/* Meta */}
      <td className="px-4 py-3 text-right font-bold text-sm tabular-nums whitespace-nowrap" style={{ color: 'var(--theme-text)' }}>
        {formatTarget(metric, goal.targetValue)}
      </td>

      {/* Ações */}
      <td className="px-4 py-3 text-right w-14">
        <CardActionsMenu actions={actions} size="sm" data-testid={`goals-actions-${goal.id}`} />
      </td>
    </tr>
  );
};

GoalListItem.displayName = 'GoalListItem';
